/**
 * The chart kit's arithmetic — pure, no React, unit-tested (`scale.test.ts`).
 * Hand-drawn charts (the user's decision: no chart library), so every axis,
 * band and path is computed here once and every chart reads the same numbers.
 */

/** `v` mapped from `domain` onto `range`, linearly (a zero-width domain maps to the range's start). */
export function linear(domain: readonly [number, number], range: readonly [number, number]): (v: number) => number {
	const [d0, d1] = domain;
	const [r0, r1] = range;
	const span = d1 - d0;
	return (v) => (span === 0 ? r0 : r0 + ((v - d0) / span) * (r1 - r0));
}

/** The smallest "nice" number (1, 2, 2.5, 5 × 10^k) at or above `max`; 1 for nothing to show. */
export function niceMax(max: number): number {
	if (!Number.isFinite(max) || max <= 0) return 1;
	const exp = 10 ** Math.floor(Math.log10(max));
	for (const f of [1, 2, 2.5, 5, 10]) {
		const nice = f * exp;
		if (nice >= max) return nice;
	}
	return 10 * exp;
}

/** The smallest 1 / 2 / 5 × 10^k step at or above `raw`. */
function niceStep(raw: number): number {
	const exp = 10 ** Math.floor(Math.log10(raw));
	for (const f of [1, 2, 5, 10]) if (f * exp >= raw) return f * exp;
	return 10 * exp;
}

/**
 * Axis ticks from 0 to the first step at or above `max`, about `count` steps
 * of 1 / 2 / 5 × 10^k, whole numbers only (medals and events are counted,
 * never halved). Always starts at 0; the last tick is the axis' top.
 */
export function ticks(max: number, count = 4): number[] {
	if (!Number.isFinite(max) || max <= 0) return [0, 1];
	const step = Math.max(1, niceStep(max / Math.max(1, count)));
	const top = Math.ceil(max / step) * step;
	const out: number[] = [];
	for (let v = 0; v <= top; v += step) out.push(v);
	return out;
}

export interface Band {
	/** Distance from one band's start to the next. */
	step: number;
	/** The band's drawn width (step minus the padding). */
	bandwidth: number;
	/** Where band `i` starts. */
	x: (i: number) => number;
}

/** `n` equal bands across `size`, `padding` (0–1) of each step left as air, split evenly on both sides. */
export function band(n: number, size: number, padding = 0.2): Band {
	const step = n > 0 ? size / n : size;
	const bandwidth = step * (1 - Math.min(Math.max(padding, 0), 0.95));
	return { step, bandwidth, x: (i) => i * step + (step - bandwidth) / 2 };
}

export interface StackSegment {
	start: number;
	end: number;
	value: number;
}

/** Running totals: each value's start and end along one bar (negative values are treated as 0). */
export function stack(values: readonly number[]): StackSegment[] {
	let at = 0;
	return values.map((raw) => {
		const value = Math.max(0, raw);
		const seg = { start: at, end: at + value, value };
		at += value;
		return seg;
	});
}

/** `part` of `whole` as 0–1, 0 when there is no whole. */
export const share = (part: number, whole: number): number => (whole > 0 ? Math.min(1, Math.max(0, part / whole)) : 0);

/**
 * An SVG arc (a ring segment drawn with a stroke) on a circle at (`cx`, `cy`)
 * of radius `r`, from `start` to `end` as fractions of a full turn, clockwise
 * from 12 o'clock. A full turn is two half arcs (one arc command cannot draw
 * a closed circle).
 */
export function arcPath(cx: number, cy: number, r: number, start: number, end: number): string {
	const a0 = Math.max(0, Math.min(1, start));
	const a1 = Math.max(a0, Math.min(1, end));
	if (a1 - a0 <= 0) return "";
	const point = (f: number) => {
		const angle = f * 2 * Math.PI - Math.PI / 2;
		return `${round(cx + r * Math.cos(angle))} ${round(cy + r * Math.sin(angle))}`;
	};
	if (a1 - a0 >= 0.9999) {
		return `M ${point(a0)} A ${r} ${r} 0 1 1 ${point(a0 + 0.5)} A ${r} ${r} 0 1 1 ${point(a0)}`;
	}
	const large = a1 - a0 > 0.5 ? 1 : 0;
	return `M ${point(a0)} A ${r} ${r} 0 ${large} 1 ${point(a1)}`;
}

/**
 * A sparkline's path through `values`, left to right across `w`, 0 at the
 * bottom of `h` and the largest value at the top (`pad` kept free all round
 * so a 2px line and its end dot are not clipped). Empty for no values; one
 * value draws a flat line.
 */
export function sparkPath(values: readonly number[], w: number, h: number, pad = 2): string {
	if (values.length === 0) return "";
	const max = Math.max(...values, 0);
	const x = values.length === 1 ? () => w / 2 : linear([0, values.length - 1], [pad, w - pad]);
	const y = linear([0, max > 0 ? max : 1], [h - pad, pad]);
	if (values.length === 1) {
		const yy = round(y(values[0] ?? 0));
		return `M ${pad} ${yy} L ${round(w - pad)} ${yy}`;
	}
	return values.map((v, i) => `${i === 0 ? "M" : "L"} ${round(x(i))} ${round(y(v))}`).join(" ");
}

/** The end point of `sparkPath` (for the end dot). */
export function sparkEnd(values: readonly number[], w: number, h: number, pad = 2): { x: number; y: number } | null {
	if (values.length === 0) return null;
	const max = Math.max(...values, 0);
	const y = linear([0, max > 0 ? max : 1], [h - pad, pad]);
	return { x: values.length === 1 ? round(w - pad) : round(w - pad), y: round(y(values[values.length - 1] ?? 0)) };
}

/**
 * A count's step on a sequential scale of `levels` steps: 0 for nothing, else
 * 1 … `levels` by its share of `max` (rounded up, so the smallest count is
 * still visibly not-zero).
 */
export function heatLevel(value: number, max: number, levels = 5): number {
	if (value <= 0 || max <= 0) return 0;
	return Math.min(levels, Math.max(1, Math.ceil((value / max) * levels)));
}

const round = (n: number): number => Math.round(n * 100) / 100;
